var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import styled from 'styled-components';
import { interpolateLab, range } from 'd3';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Axis } from '../Axis';
import { Canvas } from '../components/Canvas';
import { useCharts } from '../components/Charts';
import { defaultMargins } from '../utils';
import { Group } from '@visx/group';
import { sortAlphabetically } from './utils/sortAlphabetically';
import { Legend } from '../components/Legend';
import { GridColumns } from '../Grid';
import { getAxesConfig } from './utils/getAxesConfig';
import { BarGroupsHorizontal } from './components/BarGroupsHorizontal';
import { palette } from '../../constants';
function calculateHeight(groupCount, barCount, barHeight, barPadding) {
    var bars = groupCount * barCount;
    var padding = typeof barPadding === 'number'
        ? 2 * barPadding
        : 2 * (barPadding.inner + barPadding.outer);
    var rawHeight = bars * barHeight;
    return rawHeight + rawHeight * padding;
}
var ScrollContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin-top: ", ";\n  height: ", "px;\n  overflow-y: ", ";\n  overflow-x: hidden;\n"], ["\n  margin-top: ", ";\n  height: ", "px;\n  overflow-y: ", ";\n  overflow-x: hidden;\n"])), function (_a) {
    var margins = _a.margins;
    return margins.top + "px";
}, function (props) { return props.height; }, function (props) { return (props.scrollable ? 'auto' : 'hidden'); });
export function GroupedBarHorizontal(props) {
    var marginProps = props.margins, height = props.height, series = props.series, accessors = props.accessors, defaultActive = props.defaultActive, _a = props.colorPalette, colorPalette = _a === void 0 ? [palette.orange4, palette.orange8] : _a, crosshatchColor = props.crosshatchColor, _b = props.axesProps, axesProps = _b === void 0 ? {} : _b, _c = props.hideGrid, hideGrid = _c === void 0 ? false : _c, tooltip = props.tooltip, legendFormat = props.legendFormat, _d = props.barHeight, barHeight = _d === void 0 ? 30 : _d, _e = props.barPadding, barPadding = _e === void 0 ? 0.1 : _e, order = props.order;
    // dimensions
    var width = useCharts().width;
    var margins = defaultMargins(marginProps);
    var boundedWidth = width - margins.left - margins.right;
    var boundedHeight = height - margins.top - margins.bottom;
    // legend + colour keys
    var groupKeys = React.useMemo(function () {
        var keySet = new Set();
        Object.values(series).forEach(function (s) {
            Object.keys(accessors.data(s)).forEach(function (k) { return keySet.add(k); });
        });
        return sortAlphabetically(Array.from(keySet.values()));
    }, [accessors, series]);
    var _f = React.useState(new Set(defaultActive !== null && defaultActive !== void 0 ? defaultActive : groupKeys)), activeSet = _f[0], setActiveSet = _f[1];
    // * DOMAINS
    var yDomain = React.useMemo(function () {
        if (order !== undefined) {
            // Safeguard: filter out invalid keys to avoid error
            return order.filter(function (key) { return series[key] !== undefined; });
        }
        return Object.keys(series);
    }, [series, order]);
    var yAxisDomain = React.useMemo(function () { return yDomain.map(function (id) { return accessors.name(series[id]); }); }, [series, yDomain]);
    var xDomain = React.useMemo(function () {
        var yDomainMax = Math.max.apply(Math, Object.values(series).map(function (s) {
            var activeSeries = Object.entries(accessors.data(s))
                .filter(function (_a) {
                var key = _a[0];
                return activeSet.has(key);
            })
                .map(function (_a) {
                var _b, _c;
                var _ = _a[0], value = _a[1];
                var xValue = accessors.x(value);
                var x1Value = (_c = (_b = accessors.x1) === null || _b === void 0 ? void 0 : _b.call(accessors, value)) !== null && _c !== void 0 ? _c : 0;
                return xValue + x1Value;
            });
            return Math.max.apply(Math, activeSeries);
        }));
        return [0, yDomainMax];
    }, [series, accessors, activeSet]);
    // * DIMENSIONS
    var chartHeight = React.useMemo(function () {
        var finalHeight = calculateHeight(yDomain.length, activeSet.size, barHeight, barPadding);
        return finalHeight;
    }, [series, groupKeys, barHeight, barPadding, activeSet]);
    // * SCALES
    var xScale = React.useMemo(function () {
        return scaleLinear({
            domain: xDomain
        })
            .range([0, boundedWidth])
            .nice();
    }, [boundedWidth, xDomain]);
    var yScale = React.useMemo(function () {
        return scaleBand({
            domain: yDomain,
            padding: 0
        }).range([0, chartHeight]);
    }, [chartHeight, yDomain]);
    var yAxisScale = React.useMemo(function () {
        return scaleBand({
            domain: yAxisDomain,
            padding: 0
        }).range([0, chartHeight]);
    }, [chartHeight, yAxisDomain]);
    var colorScale = React.useMemo(function () {
        return scaleOrdinal({
            domain: groupKeys,
            range: range(groupKeys.length).map(scaleLinear({
                domain: [0, groupKeys.length - 1],
                range: colorPalette
            }).interpolate(interpolateLab))
        });
    }, [colorPalette, groupKeys]);
    var axesConfig = React.useMemo(function () { return getAxesConfig(axesProps, yAxisScale, xScale, chartHeight, margins); }, [axesProps, yAxisScale, xScale, chartHeight, margins]);
    // handlers
    var handleLegendClick = function (activeSet) {
        setActiveSet(activeSet);
    };
    var handleBarClick = function (series) {
        if (props.onBarClick === undefined)
            return;
        props.onBarClick(series);
    };
    // create a transformation for legends based on groupedKeys
    var legendSeries = React.useMemo(function () {
        return groupKeys.reduce(function (acc, key) {
            acc[key] = {
                key: key,
                name: key
            };
            return acc;
        }, {});
    }, [groupKeys]);
    var isScrollable = React.useMemo(function () { return chartHeight > boundedHeight; }, [
        chartHeight,
        boundedHeight
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ScrollContainer, { margins: margins, height: isScrollable ? boundedHeight : chartHeight, scrollable: isScrollable },
            React.createElement(Canvas, { height: chartHeight, margins: __assign(__assign({}, margins), { top: 0, bottom: 0 }) },
                !hideGrid && (React.createElement(GridColumns, { scale: xScale, numTicks: axesConfig.bottom.numTicks })),
                React.createElement(BarGroupsHorizontal, { series: series, accessors: accessors, xScale: xScale, yScale: yScale, colorScale: colorScale, crosshatchColor: crosshatchColor, activeSet: activeSet, onClick: handleBarClick, boundedHeight: chartHeight, boundedWidth: boundedWidth, tooltip: tooltip, barPadding: barPadding }),
                React.createElement(Group, null,
                    React.createElement(Axis, __assign({}, axesConfig.left, { margins: __assign(__assign({}, axesConfig.left.margins), { bottom: 0 }) }))))),
        React.createElement(Axis, __assign({}, axesConfig.bottom, { isCanvasChild: false, margins: __assign(__assign({}, axesConfig.bottom.margins), { top: 0 }), height: margins.bottom })),
        React.createElement(Legend, { series: legendSeries, accessors: {
                key: function (d) { return d.key; },
                name: function (d) { return d.name; }
            }, activeSet: activeSet, onToggle: handleLegendClick, colorScheme: colorScale, legendFormat: legendFormat, marginLeft: margins.left, marginRight: margins.right })));
}
var templateObject_1;
//# sourceMappingURL=GroupedBarHorizontal.js.map